/**
 * 📍🕒 GPS Proof-of-Presence Card
 *
 * User flow:
 *   1. Tap "Check in at Gym" or "Check in at Grocery"
 *   2. Browser asks for GPS permission → sends coords to backend
 *   3. Card enters LIVE mode with a countdown to the next tier
 *   4. Heartbeat every 60s auto-updates dwell time
 *   5. Tap "End Check-in" → tier + reward drop
 *
 * Privacy escape hatch: "No GPS? I was there" opens the manual fallback
 * modal (50 % of Quick reward, optional photo url paste).
 *
 * Owner canon: `user accessibility and control is priority` — nothing
 * about this UI hides what it's doing with the phone's location.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useGeolocation() {
  const [pos, setPos] = useState(null);
  const [err, setErr] = useState(null);
  const request = () => new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      const msg = "Location isn't available on this device.";
      setErr(msg); reject(new Error(msg)); return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const c = { lat: p.coords.latitude, lng: p.coords.longitude,
                    accuracy: p.coords.accuracy };
        setPos(c); resolve(c);
      },
      (e) => {
        const msg = e.code === 1 ? "Location permission blocked."
                  : e.code === 2 ? "Location currently unavailable."
                  : "Location request timed out.";
        setErr(msg); reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 15_000 },
    );
  });
  return { pos, err, request };
}

function fmtDwell(min) {
  if (min == null) return "0 min";
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m/60)}h ${m%60}m`;
}

export default function GpsCheckInCard() {
  const [active, setActive]           = useState(null);
  const [config, setConfig]           = useState(null);
  const [history, setHistory]         = useState([]);
  const [busy, setBusy]               = useState(false);
  const [manualOpen, setManualOpen]   = useState(false);
  const [manualType, setManualType]   = useState("gym");
  const [manualNote, setManualNote]   = useState("");
  const [manualPhoto, setManualPhoto] = useState("");
  const [manualBusy, setManualBusy]   = useState(false);
  const geo = useGeolocation();
  const beatRef = useRef(null);

  const refresh = async () => {
    try {
      const [a, c, h] = await Promise.all([
        axios.get(`${API}/gps/active`),
        axios.get(`${API}/gps/config`),
        axios.get(`${API}/gps/history?days=14`),
      ]);
      setActive(a.data?.active || null);
      setConfig(c.data || null);
      setHistory(h.data?.entries || []);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { refresh(); }, []);

  // Heartbeat loop while a check-in is active.
  useEffect(() => {
    if (beatRef.current) { clearInterval(beatRef.current); beatRef.current = null; }
    if (!active || active.ended_at) return;
    const beat = async () => {
      try {
        const coord = await geo.request();
        const r = await axios.post(`${API}/gps/heartbeat`, {
          checkin_id: active.checkin_id,
          lat: coord.lat, lng: coord.lng,
        });
        setActive(r.data);
      } catch (e) {
        // Silent — we retry on the next tick.
      }
    };
    beatRef.current = setInterval(beat, 60_000);       // once a minute
    return () => { if (beatRef.current) clearInterval(beatRef.current); };
  }, [active?.checkin_id]);

  const startAt = async (venue_type) => {
    setBusy(true);
    try {
      const coord = await geo.request();
      const r = await axios.post(`${API}/gps/start`, {
        venue_type, lat: coord.lat, lng: coord.lng,
      });
      setActive(r.data);
      toast.success(`Checked in at ${r.data.venue_label}. Timer running.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn't start check-in.");
    } finally { setBusy(false); }
  };

  const endActive = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const coord = await geo.request().catch(() => ({}));
      const r = await axios.post(`${API}/gps/end`, {
        checkin_id: active.checkin_id,
        lat: coord.lat, lng: coord.lng,
      });
      setActive(null);
      await refresh();
      const rw = r.data?.reward;
      if (rw?.tier) {
        toast.success(
          `${rw.tier_label} tier · +${rw.chits || 0} chits, +${rw.pet_xp || 0} pet XP`
          + (rw.first_trip_bonus_applied ? " · 1.5× first trip!" : "")
        );
      } else {
        toast.info(rw?.message || "Ended — try to stay a little longer next time.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn't end check-in.");
    } finally { setBusy(false); }
  };

  const submitManual = async () => {
    setManualBusy(true);
    try {
      const r = await axios.post(`${API}/gps/manual`, {
        venue_type: manualType,
        note:       manualNote,
        photo_url:  manualPhoto || null,
      });
      const rw = r.data?.reward;
      toast.success(`Manual check-in logged · +${rw.chits} chits, +${rw.pet_xp} pet XP`);
      setManualOpen(false); setManualNote(""); setManualPhoto("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn't log manual check-in.");
    } finally { setManualBusy(false); }
  };

  const nextTier    = active?.next_tier;
  const isActive    = !!active && !active.ended_at;
  const geofenceOk  = active?.in_geofence !== false;

  return (
    <div className="rounded-2xl border border-sky-500/30 bg-gradient-to-br from-[#04121b] via-[#050a0f] to-[#0a1b2b] p-4 mb-6 shadow-[0_0_20px_rgba(56,189,248,0.08)]"
         data-testid="gps-checkin-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="text-2xl">📍</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-sky-300/80 font-bold">
              Proof-of-Presence
            </div>
            <div className="text-[10px] text-neutral-500">
              Real dwell time · phone stays honest · 1.5× first trip of the day
            </div>
          </div>
        </div>
      </div>

      {/* Active check-in */}
      {isActive ? (
        <div className="rounded-xl border border-sky-500/40 bg-black/50 p-4 mb-3"
             data-testid="gps-active">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm font-bold text-sky-200">
              {active.venue_icon} {active.venue_label} — live
            </div>
            <div className={`text-[10px] uppercase tracking-widest font-bold ${
              geofenceOk ? "text-emerald-300" : "text-amber-300"}`}
                 data-testid="gps-fence-badge">
              {geofenceOk ? "● IN RANGE" : "○ OUTSIDE"}
            </div>
          </div>
          <div className="flex items-baseline gap-3 mb-2">
            <div className="text-4xl font-black text-white tabular-nums"
                 style={{ fontFamily: "Anton, sans-serif" }}
                 data-testid="gps-dwell-min">
              {fmtDwell(active.dwell_min)}
            </div>
            {nextTier && (
              <div className="text-[11px] text-neutral-400">
                {nextTier.minutes_remaining} min to
                <span className="ml-1 text-amber-300 font-bold uppercase tracking-widest">
                  {nextTier.key}
                </span>
              </div>
            )}
          </div>
          {/* Tier progress bar */}
          {active.tiers?.length > 0 && (
            <div className="mb-3">
              <div className="h-2 rounded-full bg-neutral-900 overflow-hidden flex">
                {active.tiers.map((t, i) => {
                  const filled = active.dwell_min >= t.min_min;
                  return (
                    <div key={t.key} className={`h-full flex-1 mr-[1px] transition-all ${
                      filled ? "bg-sky-400" : "bg-neutral-800"}`}
                      data-testid={`gps-tier-seg-${t.key}`} />
                  );
                })}
              </div>
              <div className="flex justify-between text-[9px] uppercase tracking-widest text-neutral-500 mt-1">
                {active.tiers.map((t) => (
                  <span key={t.key}>{t.key}</span>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={endActive}
            disabled={busy}
            className="w-full py-2.5 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-600 text-white font-black tracking-wide hover:brightness-110 disabled:opacity-60"
            data-testid="gps-end-btn"
          >
            {busy ? "Ending…" : "🏁 End Check-in & Claim Reward"}
          </button>
        </div>
      ) : (
        /* Idle — start options */
        <div className="grid grid-cols-2 gap-2 mb-3">
          {Object.entries(config?.venues || {}).map(([key, v]) => (
            <button
              key={key}
              onClick={() => startAt(key)}
              disabled={busy}
              className="rounded-xl border border-sky-500/30 bg-black/40 hover:border-sky-400/70 hover:bg-black/60 p-3 text-left transition disabled:opacity-50"
              data-testid={`gps-start-${key}`}
            >
              <div className="text-2xl mb-1">{v.icon}</div>
              <div className="text-sm font-bold text-white">{v.label}</div>
              <div className="text-[10px] text-neutral-400 leading-tight">
                {v.tiers?.map((t) => `${t.min_min}m`).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Manual fallback */}
      {!isActive && (
        <button
          onClick={() => setManualOpen(true)}
          className="w-full text-[11px] text-neutral-400 hover:text-neutral-200 underline underline-offset-2 mb-3"
          data-testid="gps-manual-open"
        >
          No GPS? I was there — log it manually
        </button>
      )}

      {/* Recent history */}
      {history?.length > 0 && (
        <div className="pt-2 border-t border-neutral-800">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">
            Recent
          </div>
          <div className="space-y-1 max-h-32 overflow-auto">
            {history.slice(0, 6).map((h) => (
              <div key={h.checkin_id} className="flex items-center justify-between text-[11px]"
                   data-testid="gps-history-row">
                <span className="text-neutral-300">
                  {h.venue_icon} {h.venue_name || h.venue_label}
                  {h.manual && <span className="text-neutral-500 ml-1">(manual)</span>}
                </span>
                <span className="text-neutral-500">
                  {h.tier ? (
                    <span className="text-sky-300 uppercase tracking-widest">
                      {h.tier_label || h.tier}
                    </span>
                  ) : (
                    <span>{fmtDwell(h.dwell_min)}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual modal */}
      {manualOpen && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4"
             onClick={() => setManualOpen(false)}
             data-testid="gps-manual-modal">
          <div className="rounded-2xl bg-neutral-950 border border-sky-500/40 max-w-md w-full p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold text-sky-200 mb-1">Manual Check-in</div>
            <div className="text-[11px] text-neutral-400 mb-3">
              50 % of Quick reward · once per PT day · optional photo proof
            </div>
            <div className="flex gap-2 mb-3">
              {Object.entries(config?.venues || {}).map(([key, v]) => (
                <button key={key}
                  onClick={() => setManualType(key)}
                  className={`flex-1 py-2 rounded-lg border text-sm ${
                    manualType === key
                      ? "bg-sky-500/25 border-sky-400/70 text-sky-100"
                      : "bg-black/40 border-neutral-800 text-neutral-400"}`}
                  data-testid={`gps-manual-type-${key}`}>
                  {v.icon} {v.label}
                </button>
              ))}
            </div>
            <textarea
              placeholder="Where were you? (optional)"
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              maxLength={280}
              className="w-full h-16 mb-2 px-3 py-2 rounded-lg bg-black/50 border border-neutral-800 text-sm text-neutral-100 focus:border-sky-500/60 focus:outline-none"
              data-testid="gps-manual-note"
            />
            <input
              type="url"
              placeholder="Photo URL (receipt / gym sign) — optional"
              value={manualPhoto}
              onChange={(e) => setManualPhoto(e.target.value)}
              className="w-full mb-3 px-3 py-2 rounded-lg bg-black/50 border border-neutral-800 text-sm text-neutral-100 focus:border-sky-500/60 focus:outline-none"
              data-testid="gps-manual-photo"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setManualOpen(false)}
                className="flex-1 py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-300 text-sm"
                data-testid="gps-manual-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitManual}
                disabled={manualBusy}
                className="flex-1 py-2 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-600 text-white font-bold text-sm disabled:opacity-60"
                data-testid="gps-manual-submit"
              >
                {manualBusy ? "Submitting…" : "Log It"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
